import { useEffect } from 'react';
import { takeReturnTo, useAuthStore } from '@/features/auth/store/authStore';
import { LoginScreen } from '@/features/auth/components/LoginScreen';
import { Layout } from '@/app/Layout';

export default function App() {
  const user = useAuthStore((s) => s.user);
  const booting = useAuthStore((s) => s.booting);
  const restore = useAuthStore((s) => s.restore);
  const ssoExchange = useAuthStore((s) => s.ssoExchange);

  useEffect(() => {
    // How this instance signs people in — needed by the sign-in screen and by
    // Settings (no password buttons under one login). Never blocks the boot.
    void useAuthStore.getState().loadOptions();
    void (async () => {
      // Arriving from the CG Team tile as #/sso/<pass>. Two things make the order
      // here load-bearing:
      //
      //  1. The pass is STRIPPED FROM THE ADDRESS BAR BEFORE THE AWAIT. It is a
      //     live credential for sixty seconds; it must not survive into history,
      //     a bookmark, or a screenshot of someone's screen.
      //  2. This runs before Layout mounts the router. `#/sso/…` is not one of
      //     the four addresses the router understands, so if the router saw it
      //     first it would normalise it away and the pass would vanish unspent —
      //     which is exactly what used to happen.
      const m = /^#\/sso\/([^/?#]+)/.exec(window.location.hash);
      if (m) {
        // One login: someone who followed a task link, was sent to CG Team to sign
        // in and came back through the tile lands on that task. The pass is
        // replaced by that address HERE, before the await: once the session
        // lands React mounts the router straight away, and it reads the address
        // bar as its starting point — setting it afterwards is too late.
        const back = takeReturnTo() ?? '#/';
        history.replaceState(null, '', window.location.pathname + window.location.search + back);
        if (await ssoExchange(decodeURIComponent(m[1]))) return;
        // Fell through on purpose: restore() below still runs, so an existing
        // valid session is not thrown away just because the hand-off failed.
      }
      await restore();
    })();
  }, [restore, ssoExchange]);

  if (booting) {
    return (
      // The brand thread starts at frame zero: the login screen's wordmark on the
      // same bg-page canvas, so boot → login → app is one continuous surface
      // instead of a flash of off-brand sidebar green.
      <div className="flex min-h-dvh flex-col items-center justify-center gap-4 bg-page">
        <h1 className="text-2xl font-bold tracking-tight text-ink">Chronic<span className="text-brand">Guru</span></h1>
        <span className="h-5 w-5 animate-spin rounded-full border-2 border-line border-t-brand" />
      </div>
    );
  }

  if (!user) return <LoginScreen />;

  return <Layout />;
}
